import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { confirmacaoConfere } from '@tipscore/shared'
import { type FormEvent, useState } from 'react'
import { AccountAvatar } from '../../components/AccountAvatar'
import { BotaoAcao } from '../../components/BotaoAcao'
import { IconCopy } from '../../components/icons'
import { Modal } from '../../components/Modal'
import { dayWithYear, haQuantoTempo } from '../../lib/format'
import { t, tRico } from '../../lib/i18n'
import { useWorkspaceRole } from '../../lib/role'
import { useTRPC } from '../../lib/trpc'

export const Route = createFileRoute('/_app/contas')({ component: Settings })

/**
 * Até quando um erro de entrega do webhook ainda diz algo sobre o agora.
 *
 * O Telegram reentrega em segundos; uma hora de silêncio depois do erro
 * significa que ele voltou. Acima disso é histórico, não estado.
 */
const JANELA_DE_ERRO_MS = 60 * 60 * 1000

function Settings() {
  const { isAdmin, isLoading } = useWorkspaceRole()

  // O item some do menu para quem não é admin, mas a rota continua digitável.
  // Sem esta guarda a tela abriria só com erros de 403.
  if (!isAdmin) {
    return (
      <div className="grid h-full place-items-center px-5">
        <p className="max-w-sm text-center text-sm text-ink-3">
          {isLoading
            ? t('Carregando…')
            : t(
                'Conectar e configurar contas do Telegram é coisa de administrador. Fale com quem administra o workspace.',
              )}
        </p>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="fim-seguro mx-auto max-w-3xl px-5 pt-4 md:pt-8">
        <h1 className="font-display text-2xl">{t('Contas do Telegram')}</h1>
        <BotsSection />
        <ChannelsSection />
        <BotChannelForm />
      </div>
    </div>
  )
}

function BotsSection() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [token, setToken] = useState('')

  const bots = useQuery(trpc.bot.list.queryOptions())
  const [confirmandoRemocao, setConfirmandoRemocao] = useState<string | null>(null)

  const remove = useMutation({
    ...trpc.bot.remove.mutationOptions(),
    onSuccess: () => {
      setConfirmandoRemocao(null)
      queryClient.invalidateQueries({ queryKey: [['bot']] })
    },
  })

  const register = useMutation({
    ...trpc.bot.register.mutationOptions(),
    onSuccess: () => {
      setToken('')
      queryClient.invalidateQueries({ queryKey: [['bot']] })
    },
  })

  function submit(event: FormEvent) {
    event.preventDefault()
    register.mutate({ token })
  }

  return (
    <section className="mt-8">
      <h2 className="text-md font-semibold text-ink">{t('Bots do Telegram')}</h2>
      <p className="mt-1 text-xs leading-relaxed text-ink-2">
        {tRico(
          'Crie um bot no {botfather}, ative o {business} em Bot Settings e cole o token abaixo. O token é cifrado antes de ser gravado e nunca é exibido de volta.',
          {
            botfather: <strong>@BotFather</strong>,
            business: <strong>Business Mode</strong>,
          },
        )}
      </p>

      <form onSubmit={submit} className="mt-4 flex gap-2">
        <input
          value={token}
          onChange={(e) => setToken(e.target.value)}
          required
          placeholder="123456789:AAF-..."
          className="flex-1 rounded-lg border border-line-strong bg-surface px-3 py-2 text-sm text-ink"
        />
        <button
          type="submit"
          disabled={register.isPending}
          className="shrink-0 rounded-lg bg-accent px-4 text-sm font-medium text-on-accent disabled:opacity-50"
        >
          {register.isPending ? t('Validando…') : t('Conectar')}
        </button>
      </form>

      {register.error && (
        <p role="alert" className="mt-2 text-xs text-danger">
          {t(register.error.message)}
        </p>
      )}

      {remove.error && (
        <p role="alert" className="mt-2 rounded-lg bg-danger-soft px-3 py-2 text-xs text-danger">
          {t(remove.error.message)}
        </p>
      )}

      {register.data && (
        <div className="mt-3 space-y-2">
          {!register.data.canConnectToBusiness && (
            <Alert tone="warning">
              {tRico(
                'O {business} não está ligado neste bot. Sem ele, o Telegram não permite adicioná-lo a uma conta Business. Ative em @BotFather → /mybots → Bot Settings → Business Mode.',
                { business: <strong>Business Mode</strong> },
              )}
            </Alert>
          )}
          {!register.data.webhookRegistered && (
            <Alert tone="warning">
              {tRico(
                'Bot cadastrado, mas o webhook {nao} foi registrado: o endereço público configurado não é alcançável pelo Telegram. Em desenvolvimento isso é esperado — use um túnel HTTPS para testar de ponta a ponta.',
                { nao: <strong>{t('não')}</strong> },
              )}
            </Alert>
          )}
          {register.data.canConnectToBusiness && register.data.webhookRegistered && (
            <Alert tone="ok">
              {tRico(
                'Bot conectado. Agora, no Telegram: {caminho}, adicione {arroba} e conceda as permissões.',
                {
                  caminho: <strong>{t('Configurações → Telegram Business → Chatbots')}</strong>,
                  arroba: `@${register.data.username}`,
                },
              )}
            </Alert>
          )}
        </div>
      )}

      {/* Um cartão por bot: identidade em cima, diagnóstico do webhook como
          seção do mesmo cartão — antes eram dois blocos soltos e não ficava
          claro que falavam do mesmo bot. */}
      <ul className="mt-4 space-y-3">
        {(bots.data ?? []).map((bot) => (
          <li key={bot.id} className="overflow-hidden rounded-xl border border-line bg-surface">
            <div className="flex flex-wrap items-start gap-3 px-3.5 py-3">
              <AccountAvatar
                tipo="bot"
                id={bot.id}
                nome={bot.displayName || bot.username}
                hasPhoto={bot.hasPhoto}
                className="size-11 flex-none rounded-xl text-sm"
              />

              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-baseline gap-x-2">
                  <span className="truncate text-sm font-semibold text-ink">
                    {bot.displayName || `@${bot.username}`}
                  </span>
                  <span className="truncate text-2xs text-ink-3">@{bot.username}</span>
                </span>

                {bot.shortDescription && (
                  <span className="mt-0.5 line-clamp-2 block text-2xs leading-relaxed text-ink-2">
                    {bot.shortDescription}
                  </span>
                )}

                <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-2xs text-ink-3">
                  <span>ID {bot.telegramBotId}</span>
                  <span aria-hidden>·</span>
                  <span>
                    {t('Token')} •••{bot.tokenHint}
                  </span>
                  <span aria-hidden>·</span>
                  <span>
                    {bot.channelCount === 1
                      ? t('{n} conta', { n: bot.channelCount })
                      : t('{n} contas', { n: bot.channelCount })}
                  </span>
                </span>

                {bot.lastError && (
                  <span className="mt-1 block text-2xs text-danger">{bot.lastError}</span>
                )}
              </span>

              <span className="flex flex-none flex-col items-end gap-1">
                <span
                  className="rounded-full px-2 py-0.5 text-2xs font-semibold"
                  style={{
                    color: bot.isActive ? 'var(--st-resolv)' : 'var(--ink-3)',
                    background: bot.isActive ? 'var(--st-resolv-soft)' : 'var(--surface-2)',
                  }}
                >
                  {bot.isActive ? t('Ativo') : t('Inativo')}
                </span>

                {/* Business Mode desligado é a causa mais comum de "adicionei
                    o bot e não aconteceu nada" — o Telegram nem deixa anexar
                    o bot à conta. Dizer isso aqui evita a caçada. */}
                {bot.canConnectToBusiness === false && (
                  <span
                    className="rounded-full px-2 py-0.5 text-2xs font-semibold"
                    style={{ color: 'var(--st-atend)', background: 'var(--st-atend-soft)' }}
                    title={t('Ative em @BotFather → /mybots → Bot Settings → Business Mode')}
                  >
                    Business Mode off
                  </span>
                )}

                {/* Dois passos: apagar bot derruba o webhook e desliga a
                    entrada de mensagens — não é clique que se dá sem querer. */}
                <BotaoAcao
                  variante="destrutiva"
                  onClick={() =>
                    confirmandoRemocao === bot.id
                      ? remove.mutate({ botId: bot.id })
                      : setConfirmandoRemocao(bot.id)
                  }
                  onBlur={() => setConfirmandoRemocao(null)}
                  disabled={remove.isPending}
                  className={confirmandoRemocao === bot.id ? 'border-danger/40 text-danger' : ''}
                >
                  {confirmandoRemocao === bot.id ? t('Confirmar remoção') : t('Remover')}
                </BotaoAcao>
              </span>
            </div>

            <WebhookDiagnostics botId={bot.id} username={bot.username} />
          </li>
        ))}
      </ul>
    </section>
  )
}

/**
 * O que o Telegram acha que está configurado.
 *
 * "O bot não recebe nada" tem várias causas invisíveis daqui: webhook em outro
 * endereço, `allowed_updates` sem os tipos de business, ou updates represados
 * por erro de entrega. Mostrar isso evita adivinhação.
 */
function WebhookDiagnostics({ botId, username }: { botId: string; username: string }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const info = useQuery(trpc.bot.webhookInfo.queryOptions({ botId }))

  const refresh = useMutation({
    ...trpc.bot.refreshWebhook.mutationOptions(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [['bot']] }),
  })

  const refreshProfile = useMutation({
    ...trpc.bot.refreshProfile.mutationOptions(),
    // A busca acontece na fila, então o dado chega um instante depois do
    // clique — recarregar sozinho evita a pessoa achar que não funcionou.
    onSuccess: () => {
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: [['bot']] })
        queryClient.invalidateQueries({ queryKey: [['channel']] })
      }, 1500)
    },
  })

  if (info.isPending) return null

  if (info.error) {
    return (
      <p
        role="alert"
        className="border-t border-line bg-danger-soft px-3.5 py-2.5 text-2xs text-danger"
      >
        {t('Não consegui consultar o webhook de @{bot}:', { bot: username })}{' '}
        {t(info.error.message)}
      </p>
    )
  }

  const dados = info.data
  if (!dados) return null

  const semUrl = !dados.url
  const faltando = dados.faltandoNoAllowedUpdates

  /**
   * Erro do Telegram é histórico, não estado — só conta junto com fila parada.
   *
   * O `last_error_message` do `getWebhookInfo` guarda o último erro de entrega
   * que já aconteceu naquele webhook e **não é limpo quando volta a entregar**;
   * só some ao chamar `setWebhook` de novo. Um "Connection reset by peer" de
   * semanas atrás — a conexão do Telegram para cá caiu no meio de uma entrega,
   * ele reentregou, ninguém perdeu mensagem — mantinha a conta vermelha para
   * sempre, com o bot recebendo e enviando normalmente.
   *
   * O sinal vivo é a fila: se o webhook está mesmo quebrado, o Telegram acumula
   * updates e o `pending_update_count` cresce. Erro antigo com fila zerada é
   * cicatriz; erro recente com fila represada é problema agora.
   */
  const erroEm = dados.lastErrorDate ? new Date(dados.lastErrorDate * 1000) : null
  const erroRecente = erroEm !== null && Date.now() - erroEm.getTime() < JANELA_DE_ERRO_MS
  const entregaTravada = erroRecente && dados.pendingUpdateCount > 0
  const problema = semUrl || faltando.length > 0 || entregaTravada

  return (
    <div
      className={`border-t px-3.5 py-2.5 text-2xs leading-relaxed ${
        problema ? 'border-danger/20 bg-danger-soft' : 'border-line bg-surface-2/50'
      }`}
    >
      <p className="flex flex-wrap items-center gap-2">
        <span className="font-semibold uppercase tracking-wider text-ink-3">Webhook</span>
        {problema ? (
          <span className="font-semibold text-danger">{t('precisa de atenção')}</span>
        ) : (
          <span style={{ color: 'var(--st-resolv)' }}>{t('tudo certo')}</span>
        )}
        <span className="ml-auto flex items-center gap-3">
          {/* Foto e descrição mudam no BotFather sem avisar o CRM — este
              botão é como quem administra traz a mudança para cá. */}
          <button
            type="button"
            onClick={() => refreshProfile.mutate({ botId })}
            disabled={refreshProfile.isPending}
            className="font-semibold text-ink-3 hover:text-ink disabled:opacity-50"
          >
            {refreshProfile.isPending ? t('Atualizando…') : t('Atualizar perfil')}
          </button>
          <button
            type="button"
            onClick={() => refresh.mutate({ botId })}
            disabled={refresh.isPending}
            className="font-semibold text-accent-ink hover:underline disabled:opacity-50"
          >
            {refresh.isPending ? t('Reaplicando…') : t('Reaplicar webhook')}
          </button>
        </span>
      </p>

      <dl className="mt-1.5 grid grid-cols-[110px_minmax(0,1fr)] gap-x-2 gap-y-0.5 text-ink-2">
        <dt className="text-ink-3">{t('Endereço')}</dt>
        <dd className="truncate">{dados.url || t('— nenhum registrado')}</dd>

        <dt className="text-ink-3">{t('Na fila')}</dt>
        <dd>{t('{n} update(s) aguardando entrega', { n: dados.pendingUpdateCount })}</dd>

        {dados.lastErrorMessage && (
          <>
            <dt className="text-ink-3">{t('Último erro')}</dt>
            <dd className={entregaTravada ? 'text-danger' : 'text-ink-2'}>
              {dados.lastErrorMessage}
              {erroEm && <span className="text-ink-3"> · {haQuantoTempo(erroEm)}</span>}
              {/* Sem isto a linha lê como "está quebrado", que é justamente o
                  que o erro velho não quer dizer. */}
              {!entregaTravada && <span className="text-ink-3"> · {t('já recuperado')}</span>}
            </dd>
          </>
        )}

        <dt className="text-ink-3">{t('Tipos')}</dt>
        <dd>
          {faltando.length === 0
            ? t('todos os que o CRM precisa')
            : t('faltando: {lista}', { lista: faltando.join(', ') })}
        </dd>
      </dl>

      {faltando.includes('business_connection') && (
        <p className="mt-2 text-danger">
          {tRico(
            'Sem {tipo} o CRM nunca fica sabendo que a conta foi conectada, e toda mensagem dela é descartada. Reaplique o webhook e depois remova e adicione o bot de novo em Telegram → Configurações → Telegram Business → Chatbots.',
            { tipo: <code>business_connection</code> },
          )}
        </p>
      )}
    </div>
  )
}

/**
 * Canal de bot comum. Diferente de conta business, não aparece sozinho: não
 * existe evento que anuncie "este bot agora atende aqui", então é criado à mão.
 */
function BotChannelForm() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const bots = useQuery(trpc.bot.list.queryOptions())
  const inboxes = useQuery(trpc.inbox.list.queryOptions())
  const channels = useQuery(trpc.channel.list.queryOptions())

  const [botId, setBotId] = useState('')
  const [inboxId, setInboxId] = useState('')
  const [name, setName] = useState('')

  const create = useMutation({
    ...trpc.channel.createBotChannel.mutationOptions(),
    onSuccess: () => {
      setName('')
      queryClient.invalidateQueries({ queryKey: [['channel']] })
    },
  })

  // Bot que já tem canal não aparece: um canal por bot, senão a ingestão fica
  // ambígua (a mensagem chega identificada só pelo bot).
  const usados = new Set(
    (channels.data ?? []).filter((row) => row.type === 'bot').map((row) => row.botUsername),
  )
  const disponiveis = (bots.data ?? []).filter((row) => !usados.has(row.username))

  if (disponiveis.length === 0) return null

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        create.mutate({ botId, inboxId, name })
      }}
      className="mt-4 rounded-lg border border-dashed border-line-strong p-3"
    >
      <p className="text-xs font-semibold">{t('Adicionar canal de bot')}</p>
      {/* "payload" não aparece na tela: quem lê isto administra uma operação
          de vendas, não o Worker. O que importa para ela é que o link não se
          adultera. */}
      <p className="mt-1 text-2xs leading-relaxed text-ink-3">
        {tRico(
          'Para atender por um bot comum, em vez de conta business. O lead precisa dar {start} primeiro, mas a atribuição de campanha fica {exata} — o link não pode ser adulterado.',
          { start: <code>/start</code>, exata: <strong>{t('exata')}</strong> },
        )}
      </p>

      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        <select
          value={botId}
          onChange={(event) => setBotId(event.target.value)}
          required
          className="rounded-md border border-line-strong bg-surface px-2 py-1.5 text-xs"
        >
          <option value="">{t('Bot…')}</option>
          {disponiveis.map((row) => (
            <option key={row.id} value={row.id}>
              @{row.username}
            </option>
          ))}
        </select>

        <select
          value={inboxId}
          onChange={(event) => setInboxId(event.target.value)}
          required
          className="rounded-md border border-line-strong bg-surface px-2 py-1.5 text-xs"
        >
          <option value="">{t('Inbox…')}</option>
          {(inboxes.data ?? []).map((row) => (
            <option key={row.id} value={row.id}>
              {row.name}
            </option>
          ))}
        </select>

        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
          placeholder={t('Nome do canal')}
          className="rounded-md border border-line-strong bg-surface px-2 py-1.5 text-xs"
        />
      </div>

      {create.error && (
        <p role="alert" className="mt-2 text-2xs text-danger">
          {t(create.error.message)}
        </p>
      )}

      <button
        type="submit"
        disabled={create.isPending}
        className="mt-3 rounded-md bg-accent px-3 py-1.5 text-xs font-semibold text-on-accent disabled:opacity-50"
      >
        {create.isPending ? t('Criando…') : t('Criar canal')}
      </button>
    </form>
  )
}

interface RightCheck {
  key: string
  label: string
  impact: string
  granted: boolean
  critical: boolean
}

/**
 * Direitos reorganizados por severidade, não em lista corrida de ✓/✕: o que
 * falta — e o que isso custa — vem primeiro; o que está concedido vira uma
 * linha de chips que o olho pula quando está tudo bem.
 */
function ChannelRights({ checks }: { checks: RightCheck[] }) {
  const criticosFaltando = checks.filter((check) => !check.granted && check.critical)
  const opcionaisFaltando = checks.filter((check) => !check.granted && !check.critical)
  const concedidos = checks.filter((check) => check.granted)

  return (
    <div className="space-y-2 px-3.5 py-3">
      {criticosFaltando.length > 0 && (
        <div className="rounded-lg bg-danger-soft px-3 py-2">
          <p className="text-2xs font-bold uppercase tracking-wider text-danger">
            {t('Sem estes direitos o CRM não funciona')}
          </p>
          {/* `label` e `impact` chegam prontos do servidor, em português
              (`REQUIRED_RIGHTS`, em routers/channel.ts). O servidor continua
              falando português e quem traduz é a tela, na hora de exibir —
              a frase em português é a chave do catálogo. */}
          <ul className="mt-1 space-y-1">
            {criticosFaltando.map((check) => (
              <li key={check.key} className="text-2xs leading-relaxed text-danger">
                <strong>{t(check.label)}</strong> — {t(check.impact)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {opcionaisFaltando.length > 0 && (
        <div
          className="rounded-lg px-3 py-2"
          style={{ background: 'var(--st-atend-soft)', color: 'var(--st-atend)' }}
        >
          <p className="text-2xs font-bold uppercase tracking-wider">{t('Limitações')}</p>
          <ul className="mt-1 space-y-1">
            {opcionaisFaltando.map((check) => (
              <li key={check.key} className="text-2xs leading-relaxed">
                <strong>{t(check.label)}</strong> — {t(check.impact)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {concedidos.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {criticosFaltando.length === 0 && opcionaisFaltando.length === 0 && (
            <span className="text-2xs font-semibold" style={{ color: 'var(--st-resolv)' }}>
              {t('Todos os direitos concedidos')}
            </span>
          )}
          {concedidos.map((check) => (
            <span
              key={check.key}
              title={t(check.impact)}
              className="rounded-full bg-surface-2 px-2 py-0.5 text-2xs text-ink-2"
            >
              ✓ {t(check.label)}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

function ChannelsSection() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const channels = useQuery(trpc.channel.list.queryOptions())
  const inboxes = useQuery(trpc.inbox.list.queryOptions())

  const mover = useMutation({
    ...trpc.channel.update.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [['channel']] })
      queryClient.invalidateQueries({ queryKey: [['inbox']] })
    },
  })

  const conexao = useMutation({
    ...trpc.channel.setConnected.mutationOptions(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [['channel']] }),
  })

  const [removendo, setRemovendo] = useState<{ id: string; nome: string } | null>(null)

  return (
    <section className="mt-10">
      <h2 className="text-md font-semibold text-ink">{t('Contas conectadas')}</h2>
      <p className="mt-1 text-xs leading-relaxed text-ink-2">
        {t(
          'Cada conta atende por um inbox, que tem funil e equipe próprios. As contas business aparecem aqui automaticamente assim que o bot é adicionado nas configurações de Chatbots do Telegram. Trocar o inbox leva junto todo o histórico da conta e muda quem a atende.',
        )}
      </p>

      {mover.error && (
        <p role="alert" className="mt-3 rounded-lg bg-danger-soft px-3 py-2 text-2xs text-danger">
          {t(mover.error.message)}
        </p>
      )}

      {(channels.data ?? []).length === 0 && (
        <p className="mt-4 rounded-lg border border-dashed border-line px-4 py-6 text-center text-xs text-ink-3">
          {t('Nenhuma conta conectada ainda.')}
        </p>
      )}

      <ul className="mt-4 space-y-3">
        {(channels.data ?? []).map((channel) => (
          <li
            key={channel.id}
            className={`overflow-hidden rounded-xl border bg-surface ${
              channel.isEnabled ? 'border-line' : 'border-danger/30'
            }`}
          >
            {/* Identidade da conta: o anel colorido é o mesmo código de cor
                que acompanha essa conta no inbox e no perfil do lead. */}
            <div
              className={`flex flex-wrap items-center gap-3 px-3.5 py-3 ${
                channel.type === 'business' ? 'border-b border-line' : ''
              }`}
            >
              <AccountAvatar
                tipo="canal"
                id={channel.id}
                nome={channel.name}
                hasPhoto={channel.hasPhoto}
                className="size-11 flex-none rounded-full text-sm ring-2 ring-offset-2 ring-offset-surface"
                cor={channel.color}
              />

              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="truncate text-sm font-semibold text-ink">{channel.name}</span>
                  <span className="rounded-full bg-surface-2 px-2 py-0.5 text-2xs text-ink-2">
                    {channel.type === 'bot' ? t('Bot') : t('Conta business')}
                  </span>
                  {!channel.isEnabled && (
                    <span className="rounded-full bg-danger-soft px-2 py-0.5 text-2xs font-semibold text-danger">
                      {t('Desconectada')}
                    </span>
                  )}
                </span>

                {/* Nome e @ reais da conta, vindos do Telegram — o `name` do
                    canal é editável e pode não bater com a conta de verdade. */}
                {(channel.businessFirstName || channel.businessUsername) && (
                  <span className="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-2xs text-ink-2">
                    {channel.businessFirstName && (
                      <span>
                        {[channel.businessFirstName, channel.businessLastName]
                          .filter(Boolean)
                          .join(' ')}
                      </span>
                    )}
                    {channel.businessUsername && (
                      <span className="text-ink-3">@{channel.businessUsername}</span>
                    )}
                  </span>
                )}

                {channel.businessBio && (
                  <span className="mt-0.5 line-clamp-2 block text-2xs leading-relaxed text-ink-3">
                    {channel.businessBio}
                  </span>
                )}

                {/* Canal de bot: o @ e o link de quem vai conversar com ele.
                    É o que se manda para o cliente, então precisa estar a um
                    clique — antes o link do bot só existia dentro de campanha. */}
                {channel.type === 'bot' && channel.botUsername && (
                  <span className="mt-1 flex flex-wrap items-center gap-1.5">
                    <span className="text-2xs font-semibold text-ink-2">
                      @{channel.botUsername}
                    </span>
                    <code className="min-w-0 max-w-full truncate rounded bg-surface-2 px-1.5 py-0.5 text-2xs text-ink-2">
                      t.me/{channel.botUsername}
                    </code>
                    <CopiarTexto
                      valor={`https://t.me/${channel.botUsername}`}
                      rotulo={t('Copiar link do bot')}
                    />
                  </span>
                )}

                <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-2xs text-ink-3">
                  <span>
                    {channel.conversationCount === 1
                      ? t('{n} conversa', { n: channel.conversationCount })
                      : t('{n} conversas', { n: channel.conversationCount })}
                  </span>
                  {channel.unreadCount > 0 && (
                    <>
                      <span aria-hidden>·</span>
                      {/* "não lidas" solto ao lado de "conversas" lia como
                          conversas não lidas, e o número é de mensagens — dava
                          "570 conversas · 802 não lidas", que é impossível. */}
                      <span className="font-semibold text-accent-ink">
                        {channel.unreadCount === 1
                          ? t('{n} mensagem não lida', { n: channel.unreadCount })
                          : t('{n} mensagens não lidas', { n: channel.unreadCount })}
                      </span>
                    </>
                  )}
                  {channel.connectedAt && (
                    <>
                      <span aria-hidden>·</span>
                      <span>
                        {t('conectada em {data}', { data: dayWithYear(channel.connectedAt) })}
                      </span>
                    </>
                  )}
                </span>
              </span>

              <label className="flex shrink-0 items-center gap-1.5 text-2xs text-ink-3">
                {t('Inbox')}
                <select
                  value={channel.inboxId}
                  onChange={(event) =>
                    mover.mutate({ channelId: channel.id, inboxId: event.target.value })
                  }
                  className="rounded-md border border-line bg-surface px-2 py-1 text-2xs text-ink"
                >
                  {(inboxes.data ?? []).map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.name}
                    </option>
                  ))}
                </select>
              </label>

              {/* Desconectar antes de remover, e não só na ordem da tela: é a
                  saída que resolve o caso comum sem perder nada, e ficar à
                  esquerda da destrutiva é o que evita a mão pesada. */}
              <BotaoAcao
                variante={channel.isEnabled ? 'neutra' : 'positiva'}
                disabled={conexao.isPending}
                onClick={() =>
                  conexao.mutate({ channelId: channel.id, connected: !channel.isEnabled })
                }
              >
                {channel.isEnabled ? t('Desconectar') : t('Reconectar')}
              </BotaoAcao>

              <BotaoAcao
                variante="destrutiva"
                onClick={() => setRemovendo({ id: channel.id, nome: channel.name })}
              >
                {t('Remover')}
              </BotaoAcao>
            </div>

            {/* Direitos são conceito de conta business; bot comum não tem. */}
            {channel.type === 'business' && <ChannelRights checks={channel.diagnostics.checks} />}
          </li>
        ))}
      </ul>

      {removendo && (
        <RemoverConta
          channelId={removendo.id}
          nome={removendo.nome}
          onClose={() => setRemovendo(null)}
          onRemoved={() => {
            setRemovendo(null)
            queryClient.invalidateQueries({ queryKey: [['channel']] })
            queryClient.invalidateQueries({ queryKey: [['bot']] })
            queryClient.invalidateQueries({ queryKey: [['inbox']] })
          }}
        />
      )}
    </section>
  )
}

/** Copia e confirma no próprio botão — sem toast, que aqui seria exagero. */
function CopiarTexto({ valor, rotulo }: { valor: string; rotulo: string }) {
  const [copiado, setCopiado] = useState(false)

  return (
    <button
      type="button"
      title={rotulo}
      aria-label={rotulo}
      onClick={() => {
        navigator.clipboard.writeText(valor)
        setCopiado(true)
        setTimeout(() => setCopiado(false), 1200)
      }}
      className="inline-flex flex-none items-center gap-1 rounded-md border border-line bg-surface-2 px-2 py-0.5 text-2xs font-semibold text-ink-2 hover:border-line-strong hover:bg-surface-3 hover:text-ink"
    >
      <IconCopy className="size-3" />
      {copiado ? t('Copiado') : t('Copiar')}
    </button>
  )
}

function Alert({ tone, children }: { tone: 'ok' | 'warning'; children: React.ReactNode }) {
  const styles =
    tone === 'ok'
      ? { color: 'var(--st-resolv)', background: 'var(--st-resolv-soft)' }
      : { color: 'var(--st-atend)', background: 'var(--st-atend-soft)' }

  return (
    <p className="rounded-lg px-3 py-2 text-xs leading-relaxed" style={styles}>
      {children}
    </p>
  )
}

/**
 * Confirmação de remoção de conta.
 *
 * Pede o nome digitado, e não um "tem certeza?". O risco real aqui não é
 * clicar sem querer — é remover a conta errada tendo certeza da decisão.
 * Digitar o nome obriga a olhar qual está prestes a sumir.
 *
 * Os números vêm do servidor antes de liberar o botão: a cascata vai além do
 * óbvio (campanha e regra de saudação pendem do canal), e ninguém adivinha
 * isso sozinho. Confirmar sem ver é assinar em branco.
 */
function RemoverConta({
  channelId,
  nome,
  onClose,
  onRemoved,
}: {
  channelId: string
  nome: string
  onClose: () => void
  onRemoved: () => void
}) {
  const trpc = useTRPC()
  const [digitado, setDigitado] = useState('')
  // Desmarcada por padrão: o contato é o dado mais caro de recuperar.
  const [apagarContatos, setApagarContatos] = useState(false)

  const impacto = useQuery(trpc.channel.removalImpact.queryOptions({ channelId }))
  const remover = useMutation({
    ...trpc.channel.remove.mutationOptions(),
    onSuccess: onRemoved,
  })

  const dados = impacto.data
  const confere = confirmacaoConfere(digitado, nome)

  // Singular e plural são frases inteiras no catálogo: a regra de plural muda
  // de idioma para idioma, e montar a palavra por pedaços é o que impede de
  // traduzi-la.
  const linhas = dados
    ? (
        [
          { quantidade: dados.conversas, singular: 'conversa', plural: 'conversas' },
          { quantidade: dados.mensagens, singular: 'mensagem', plural: 'mensagens' },
          {
            quantidade: dados.anexos,
            singular: 'anexo (áudio, foto, arquivo)',
            plural: 'anexos (áudios, fotos, arquivos)',
          },
          { quantidade: dados.campanhas, singular: 'campanha', plural: 'campanhas' },
          {
            quantidade: dados.transmissoes,
            singular: 'transmissão e seus relatórios',
            plural: 'transmissões e seus relatórios',
          },
          {
            quantidade: dados.agendadas,
            singular: 'mensagem agendada',
            plural: 'mensagens agendadas',
          },
          {
            quantidade: dados.automacoes,
            singular: 'automação de atendimento',
            plural: 'automações de atendimento',
          },
        ] as const
      ).filter((linha) => linha.quantidade > 0)
    : []

  return (
    <Modal
      onClose={onClose}
      title={t('Remover “{nome}”', { nome })}
      description={t('A conta sai do CRM junto com tudo que depende dela. Não há como desfazer.')}
      largura="max-w-md"
    >
      {/* O `Modal` entrega os filhos num container sem recuo — quem usa é que
          põe o seu. Daí o `p-4`, que é a medida das outras telas. */}
      <div className="p-4">
        {impacto.isPending ? (
          <p className="text-xs text-ink-3">{t('Conferindo o que depende desta conta…')}</p>
        ) : (
          <>
            <p className="text-sm leading-relaxed text-ink-2">
              {tRico('Isto apaga {sempre}:', {
                sempre: <strong className="text-danger">{t('para sempre')}</strong>,
              })}
            </p>

            {linhas.length > 0 ? (
              <ul className="mt-2 space-y-1">
                {linhas.map((linha) => (
                  <li key={linha.singular} className="flex items-baseline gap-2 text-sm">
                    <strong className="font-bold tabular-nums text-danger">
                      {linha.quantidade}
                    </strong>
                    <span className="text-ink-2">
                      {t(linha.quantidade === 1 ? linha.singular : linha.plural)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-xs text-ink-3">
                {t('Esta conta ainda não tem histórico — nada além dela mesma será apagado.')}
              </p>
            )}

            {/**
             * Os contatos são decisão à parte, e desmarcada.
             *
             * Só entram os que não falam com mais nenhuma conta: quem atende
             * por Comercial e Suporte continua sendo contato depois que um dos
             * dois sair. O número vem do servidor para a caixa não prometer
             * mais do que apaga.
             */}
            <label className="mt-3 flex items-start gap-2 rounded-md bg-surface-2 px-3 py-2">
              <input
                type="checkbox"
                checked={apagarContatos}
                onChange={(event) => setApagarContatos(event.target.checked)}
                disabled={(dados?.contatosSomenteDaqui ?? 0) === 0}
                className="mt-0.5 size-3.5 flex-none accent-[var(--danger)]"
              />
              <span className="text-2xs leading-relaxed text-ink-2">
                {(dados?.contatosSomenteDaqui ?? 0) > 0
                  ? tRico(
                      dados?.contatosSomenteDaqui === 1
                        ? 'Apagar também {n} contato que fala só por esta conta. Desmarcado, ele continua em Contatos sem o histórico.'
                        : 'Apagar também {n} contatos que falam só por esta conta. Desmarcados, eles continuam em Contatos sem o histórico.',
                      {
                        n: <strong className="text-danger">{dados?.contatosSomenteDaqui}</strong>,
                      },
                    )
                  : t('Nenhum contato depende só desta conta — todos continuam em Contatos.')}
              </span>
            </label>

            <label className="mt-4 block">
              <span className="mb-1.5 block text-2xs font-semibold text-ink-2">
                {tRico('Digite {nome} para confirmar', {
                  nome: <strong className="text-ink">{nome}</strong>,
                })}
              </span>
              <input
                value={digitado}
                onChange={(event) => setDigitado(event.target.value)}
                autoComplete="off"
                className="w-full rounded-md border border-line-strong bg-surface px-3 py-2 text-sm"
              />
            </label>

            {digitado.trim().length > 0 && !confere && (
              <p className="mt-1.5 text-2xs text-ink-3">
                {tRico('Ainda não confere com {nome}.', {
                  nome: <strong className="text-ink-2">{nome}</strong>,
                })}
              </p>
            )}

            {remover.error && (
              <p role="alert" className="mt-2 text-2xs text-danger">
                {t(remover.error.message)}
              </p>
            )}

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md px-3 py-2 text-sm font-semibold text-ink-2 hover:bg-surface-2"
              >
                {t('Cancelar')}
              </button>
              <button
                type="button"
                disabled={!confere || remover.isPending}
                onClick={() =>
                  remover.mutate({ channelId, confirmName: digitado.trim(), apagarContatos })
                }
                className="rounded-md bg-danger px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
              >
                {remover.isPending ? t('Apagando…') : t('Apagar para sempre')}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
